/* ============================================================
   GOLFINGSIM — gs-motion: one shared scroll-reveal motion system.
   - Single IntersectionObserver drives [data-reveal] elements.
   - Stagger via data-reveal-stagger (ms) on a container; children
     get an incremental --gs-d delay. data-reveal-delay (ms) too.
   - Count-up engine: [data-countup] (eased, locale-formatted, once).
   - Ring-draw: [data-ring] SVG paths animate stroke-dashoffset.
   - Magnetic buttons: [data-magnetic] (pointer-fine desktop only).
   - Putting dividers + any .is-revealed-driven CSS just need the class.
   Respects prefers-reduced-motion: adds .gs-motion-off to <html>,
   reveals everything statically, and bails before any work.
   GPU transforms/opacity only — zero layout thrash, zero CLS.
   ============================================================ */
(function () {
  "use strict";

  var pointerFine = window.matchMedia("(pointer: fine)").matches;

  // NOTE (Pat's call, 2026-06-12): prefers-reduced-motion is deliberately
  // IGNORED — Windows sets the flag via "best performance" mode without user
  // intent, and the static fallback read as a broken page. Full show for all.
  // Manual kill-switch remains: add .gs-motion-off to <html>.

  // -------------------------------------------------------------
  // 1. Reveal observer (handles reveals, count-ups, rings)
  // -------------------------------------------------------------
  function prepStagger(el) {
    var step = parseInt(el.getAttribute("data-reveal-stagger"), 10);
    if (!step) return;
    var kids = el.querySelectorAll("[data-reveal]");
    for (var i = 0; i < kids.length; i++) {
      // only direct-ish descendants that don't already carry a delay
      if (kids[i].style.getPropertyValue("--gs-d")) continue;
      kids[i].style.setProperty("--gs-d", (i * step) + "ms");
    }
  }

  function reveal(el) {
    if (el.classList.contains("is-revealed")) return;
    var d = parseInt(el.getAttribute("data-reveal-delay"), 10);
    if (d && !el.style.getPropertyValue("--gs-d")) {
      el.style.setProperty("--gs-d", d + "ms");
    }
    el.classList.add("is-revealed");
  }

  // -------- count-up --------
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  function runCountUp(el) {
    if (el.dataset.gsCounted) return;
    el.dataset.gsCounted = "1";
    var target = parseFloat(el.getAttribute("data-countup"));
    if (isNaN(target)) {
      target = parseFloat((el.textContent || "").replace(/[^0-9.]/g, ""));
    }
    if (isNaN(target)) return;
    var dur = parseInt(el.getAttribute("data-countup-dur"), 10) || 1400;
    var decimals = parseInt(el.getAttribute("data-countup-decimals"), 10);
    if (isNaN(decimals)) decimals = (target % 1 !== 0) ? 1 : 0;
    var prefix = el.getAttribute("data-countup-prefix") || "";
    var suffix = el.getAttribute("data-countup-suffix") || "";
    var start = null;

    function tick(ts) {
      if (start === null) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      var val = target * easeOutCubic(p);
      el.textContent = prefix + val.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }) + suffix;
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  // -------- ring / draw (SVG stroke) --------
  function runDraw(el) {
    if (el.dataset.gsDrawn) return;
    el.dataset.gsDrawn = "1";
    // length set as CSS var so CSS can animate dashoffset → 0
    try {
      var len = el.getTotalLength ? el.getTotalLength() : 0;
      if (len) {
        // initial offset comes from CSS `stroke-dashoffset: var(--gs-len)` —
        // never set it inline, or it would override the .is-drawn rule.
        el.style.setProperty("--gs-len", len);
        el.style.strokeDasharray = len;
      }
    } catch (e) {}
    // next frame, flip the class CSS transitions against
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { el.classList.add("is-drawn"); });
    });
  }

  var io = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      if (!e.isIntersecting) continue;
      var el = e.target;
      reveal(el);
      if (el.hasAttribute("data-countup")) runCountUp(el);
      if (el.hasAttribute("data-ring") || el.hasAttribute("data-draw")) runDraw(el);
      // re-trigger count-ups/rings nested inside a revealed container
      var subs = el.querySelectorAll("[data-countup]");
      for (var j = 0; j < subs.length; j++) runCountUp(subs[j]);
      var rings = el.querySelectorAll("[data-ring],[data-draw]");
      for (var k = 0; k < rings.length; k++) runDraw(rings[k]);
      io.unobserve(el);
    }
    // trigger a touch BEFORE the element enters the viewport bottom so fast
    // mobile scrolling never lands on a half-revealed (blank) section.
  }, { threshold: 0.01, rootMargin: "0px 0px 8% 0px" });

  function collect() {
    var staggers = document.querySelectorAll("[data-reveal-stagger]");
    for (var s = 0; s < staggers.length; s++) prepStagger(staggers[s]);

    var targets = document.querySelectorAll(
      "[data-reveal],[data-countup],[data-ring],[data-draw]"
    );
    for (var i = 0; i < targets.length; i++) {
      // standalone count-ups/rings that aren't reveal targets still observed
      io.observe(targets[i]);
    }
  }

  // -------------------------------------------------------------
  // 2. Magnetic buttons (desktop pointer-fine only, ±6px)
  // -------------------------------------------------------------
  function wireMagnetic() {
    if (!pointerFine) return;
    var els = document.querySelectorAll("[data-magnetic]");
    for (var i = 0; i < els.length; i++) {
      (function (el) {
        var max = parseFloat(el.getAttribute("data-magnetic")) || 6;
        el.addEventListener("pointermove", function (ev) {
          var r = el.getBoundingClientRect();
          var dx = (ev.clientX - (r.left + r.width / 2)) / (r.width / 2);
          var dy = (ev.clientY - (r.top + r.height / 2)) / (r.height / 2);
          el.style.transform = "translate(" + (dx * max).toFixed(2) + "px," +
            (dy * max).toFixed(2) + "px)";
        });
        el.addEventListener("pointerleave", function () {
          el.style.transform = "";
        });
      })(els[i]);
    }
  }

  // -------------------------------------------------------------
  // 3. Boot
  // -------------------------------------------------------------
  function init() {
    collect();
    wireMagnetic();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-scan when Shopify section editor re-renders a section.
  document.addEventListener("shopify:section:load", function () {
    collect();
    wireMagnetic();
  });
})();
